import { ChangeDetectionStrategy, Component, effect, input, output, signal } from '@angular/core';

import { CheckboxComponent } from '@shared/components/checkbox/checkbox.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { ZERO_HYPHEN } from '@shared/constants/format';
import { LocalStorageKey } from '@shared/constants/localStorageKey';

export interface SummaryRow {
  checked: boolean;
  id: string;
  label: string;
  value1: string;
  value2: string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.self-baseline]': '!isExpanded()',
    class: 'flex flex-col gap-[4px] rounded-[8px] border bg-white p-[14px]',
  },
  imports: [CheckboxComponent, IconComponent],
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'summary-accordion',
  template: `
    @if (title()) {
      <div class="flex items-center gap-[7px]">
        <div class="text-[14px] font-medium leading-[18px] text-aux-gray-dark-1">
          {{ title() }}
        </div>
        <button
          type="button"
          (click)="toggleExpanded()"
          class="flex items-center justify-center transition-transform"
          [class.rotate-180]="isExpanded()"
        >
          <aux-icon name="ChevronDown" [size]="16" />
        </button>
      </div>
    }

    <div
      class="grid transition-all"
      [class.grid-rows-[0fr]]="!isExpanded()"
      [class.grid-rows-[1fr]]="isExpanded()"
    >
      <div class="overflow-hidden">
        <!-- Header Row -->
        <div
          class="grid grid-cols-3 gap-4 border-b border-gray-200 py-[6px] text-[14px] font-medium leading-[20px] text-aux-black"
        >
          <div>{{ columnHeader1() }}</div>
          <div class="text-center">{{ columnHeader2() }}</div>
          <div class="text-right">{{ columnHeader3() }}</div>
        </div>

        <!-- Data Rows -->
        <div>
          @for (row of rows(); track row.id) {
            <div class="grid h-[24px] grid-cols-3 items-center gap-4">
              <div class="flex items-center gap-2 pl-[4px]">
                <aux-checkbox
                  [id]="'summary-row-' + row.id"
                  [checked]="row.checked"
                  (customChange)="updateRowChecked(row.id, $event)"
                  class="text-[14px] leading-[16px] text-aux-gray-dark-1 [&_label]:ml-[4px]"
                >
                  {{ row.label }}
                </aux-checkbox>
              </div>
              <div
                class="text-center text-[14px] text-aux-gray-darkest"
                [class.font-semibold]="row.checked"
              >
                {{ row.value1 || longDash }}
              </div>
              <div
                class="text-right text-[14px] text-aux-gray-darkest"
                [class.font-semibold]="row.checked"
              >
                {{ row.value2 || longDash }}
              </div>
            </div>
          }
        </div>
      </div>
    </div>
  `,
})
export class SummaryAccordion {
  private readonly _isExpanded = signal(false);

  columnHeader1 = input<string>('Source');
  columnHeader2 = input<string>('# Activities');
  columnHeader3 = input<string>('Monthly Expense');
  localStorageKey = input<`${LocalStorageKey}`>(); // Optional localStorage key for persistence
  rows = input<SummaryRow[]>([]);
  title = input<string>('');

  rowChecked = output<{ checked: boolean; rowId: string }>();

  isExpanded = this._isExpanded.asReadonly();
  longDash = ZERO_HYPHEN;

  // Effect to handle localStorage persistence when localStorageKey changes
  private _handleLocalStorageKey = effect(() => {
    const key = this.localStorageKey();

    if (key) {
      // Restore expanded state from localStorage
      this.restoreExpandedState();
    }
  });
  // Effect to persist expanded state changes
  private _persistExpandedState = effect(() => {
    const key = this.localStorageKey();

    this._isExpanded();

    if (key) {
      this.persistExpandedState();
    }
  });

  private persistExpandedState(): void {
    try {
      localStorage.setItem(this.localStorageKey()!, JSON.stringify(this._isExpanded()));
    } catch (error) {
      console.warn('Failed to persist expanded state to localStorage:', error);
    }
  }

  private restoreExpandedState(): void {
    try {
      const stored = localStorage.getItem(this.localStorageKey()!);

      if (stored !== null) {
        const isExpanded = JSON.parse(stored);

        this._isExpanded.set(isExpanded);
      }
    } catch (error) {
      console.warn('Failed to restore expanded state from localStorage:', error);
    }
  }

  toggleExpanded(): void {
    this._isExpanded.update((expanded) => !expanded);
  }

  updateRowChecked(rowId: string, checked: boolean): void {
    this.rowChecked.emit({ checked, rowId });
  }
}
